// Copyright © 2020  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC.

import axios from 'axios'
import {ENDPOINT} from '@/shared/constants'

export const AXIOSAPI = axios.create({
  baseURL: '/'
})

AXIOSAPI.interceptors.response.use(response => {
  return response
}, function (error) {
  if (error.response.status === 401) {
    var ref = window.location.href
    var splitRef = ref.split('#/')
    var currentPath = splitRef[1]
    var basePath = splitRef[0]
    if (currentPath.includes('referer=')) {
      // In case of a double redirect, take the first one.
      currentPath = currentPath.split('referer=')[1]
    }
    var path = basePath + '#/login?referer=' + currentPath
    window.location.href = path
  }
  return Promise.reject(error)
})

class ApiRequests {
  static checkEmail (user) {
    var params = {}
    var url = ENDPOINT + 'check/email'
    return AXIOSAPI.post(url, user, { 'params': params })
  }

  static deleteApiKey (id) {
    var url = ENDPOINT + 'apikeys/' + id
    return AXIOSAPI.delete(url)
  }

  static getApiKey (id) {
    var url = ENDPOINT + 'apikeys/' + id
    return AXIOSAPI.get(url)
  }

  static postApiKey (body) {
    var url = ENDPOINT + 'apikeys'
    return AXIOSAPI.post(url, body)
  }

  static getEndpoints (site) {
    var params = {}

    if (site) {
      params.site = site
    }

    var url = ENDPOINT + 'endpoints'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static getEventSources (site) {
    var params = {}

    if (site) {
      params.site = site
    }

    var url = ENDPOINT + 'eventsources'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static getEndpoint (id) {
    var url = ENDPOINT + 'endpoints/' + id
    return AXIOSAPI.get(url)
  }

  static getEndpointConfig (id) {
    var params = {}

    var url = ENDPOINT + 'endpoints/config/' + id
    return AXIOSAPI.get(url, { 'params': params })
  }

  static putEndpointConfig (id, data) {
    var url = ENDPOINT + 'endpoints/config/' + id
    return AXIOSAPI.put(url, data)
  }

  static getEndpointStatus (id) {
    var params = {}

    var url = ENDPOINT + 'endpoints/status/' + id
    return AXIOSAPI.get(url, { 'params': params })
  }

  static queryEndpointStatus (id, limit) {
    var params = {}
    var url = ENDPOINT + 'endpoints/status'
    params.endpoint = id
    params.limit = 1
    if (limit) {
      params.limit = limit
    }
    return AXIOSAPI.get(url, { 'params': params })
  }

  static getEndpointStatusFB (id) {
    var params = {}

    var url = ENDPOINT + 'endpoints/statusfb/' + id
    return AXIOSAPI.get(url, { 'params': params })
  }

  static exceptionGet (id) {
    var url = ENDPOINT + 'exceptions/' + id
    return AXIOSAPI.get(url)
  }

  static eventsQuery (params) {
    var url = ENDPOINT + 'events'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static getEvents (site, timestart, timeend, limit) {
    var url = ENDPOINT + 'events'
    var params = {}

    if (site) {
      params.site = site
    }

    if (timestart) {
      params.after = timestart
    }

    if (timeend) {
      params.before = timeend
    }

    if (limit) {
      params.limit = limit
    }
    return AXIOSAPI.get(url, { 'params': params })
  }

  static postHttpEvent (params) {
    var url = ENDPOINT + 'messages/user'
    return AXIOSAPI.post(url, params)
  }

  static exceptionQuery (params) {
    var url = ENDPOINT + 'exceptions'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static queryLocationTags () {
    var params = {}
    var url = ENDPOINT + 'locationtags'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static getRoles () {
    var params = {}
    var url = ENDPOINT + 'roles'
    params.sort = 'name'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static reportConfigGet (id) {
    var url = ENDPOINT + 'reportconfigs/' + id
    return AXIOSAPI.get(url)
  }

  static reportConfigQuery (params) {
    var url = ENDPOINT + 'reportconfigs'
    params.sort = 'name'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static getCategories () {
    var url = ENDPOINT + 'categories'
    return AXIOSAPI.get(url)
  }

  static getSites () {
    var params = {}
    var url = ENDPOINT + 'sites'
    params.sort = 'name'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static getThings (after, site, sort) {
    var params = {}
    var url = ENDPOINT + 'things'

    if (after) {
      params.after = after
    }

    if (site) {
      params.site = site
    }

    if (sort) {
      params.sort = sort
    }

    return AXIOSAPI.get(url, { 'params': params })
  }

  static getThing (id) {
    var url = ENDPOINT + 'things/' + id
    return AXIOSAPI.get(url)
  }

  static getThingByTag (tag) {
    var url = ENDPOINT + 'things?tags=' + tag
    return AXIOSAPI.get(url)
  }

  static postThing (thing) {
    var url = ENDPOINT + 'things'
    return AXIOSAPI.post(url, thing)
  }

  static thingQuery (params) {
    var url = ENDPOINT + 'things'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static deleteUser (id) {
    var url = ENDPOINT + 'users/' + id
    return AXIOSAPI.delete(url)
  }

  static getChildren (id) {
    var url = ENDPOINT + 'things/' + id + '/children'
    return AXIOSAPI.get(url)
  }

  static getParents (id) {
    var url = ENDPOINT + 'things/' + id + '/parents'
    return AXIOSAPI.get(url)
  }

  static getUser (id) {
    var url = ENDPOINT + 'users/' + id
    return AXIOSAPI.get(url)
  }

  static getUsername (id) {
    var url = ENDPOINT + 'usernames/' + id
    return AXIOSAPI.get(url)
  }

  static getUsernames () {
    var params = {}
    var url = ENDPOINT + 'usernames'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static postUser (user, email) {
    var params = {}
    if (email) {
      params.email = true
    }
    var url = ENDPOINT + 'users'
    return AXIOSAPI.post(url, user, { 'params': params })
  }

  static putUser (user) {
    var params = {}
    var url = ENDPOINT + 'users/' + user.id
    return AXIOSAPI.put(url, user, { 'params': params })
  }

  static getZones (site, sort) {
    var params = {
      limit: 1000
    }
    if (site) {
      params.site = site
    }

    if (sort) {
      params.sort = sort
    }
    var url = ENDPOINT + 'zones'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static getZoneDurations (site, after, before) {
    var params = {}
    if (site) {
      params.site = site
    }

    if (after) {
      params.after = after
    }

    if (before) {
      params.before = before
    }

    var url = ENDPOINT + 'zonedurations'
    return AXIOSAPI.get(url, { 'params': params })
  }

  /* Image API */

  static createImage (file, config = null) {
    let url = ENDPOINT + 'images'
    return AXIOSAPI.post(url, file, config)
  }

  static getImages (params) {
    var url = ENDPOINT + 'images'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static getImageCategories (params) {
    let url = ENDPOINT + 'image-categories'
    return AXIOSAPI.get(url, { 'params': params })
  }

  static postImageCategories (body, params) {
    let url = ENDPOINT + 'image-categories'
    return AXIOSAPI.post(url, body, { 'params': params })
  }

  static getImageProperties (id) {
    let url = ENDPOINT + 'images/' + id + '/meta'
    return AXIOSAPI.get(url)
  }

  static deleteImage (id) {
    let url = ENDPOINT + 'images/' + id
    return AXIOSAPI.delete(url)
  }

  static postImage (file, tags, categories) {
    var params = {}
    var url = ENDPOINT + 'images'
    if (tags) {
      params.tags = tags
    }

    if (categories) {
      params.categories = categories
    }

    return AXIOSAPI.post(url, file, { 'params': params })
  }

  static putImageProperties (id, properties) {
    let url = ENDPOINT + 'images/' + id + '/meta'
    return AXIOSAPI.put(url, { photo: properties })
  }

  /* Maps API */

  static getMaps (params = {}) {
    var url = 'maps'
    if (params.id) {
      url = url + '/' + params.id
    }
    return AXIOSAPI.get(url, { baseURL: ENDPOINT, 'params': params })
  }

  static getMap (id) {
    var url = ENDPOINT + 'maps/' + id
    return AXIOSAPI.get(url)
  }

  static createMaps (data) {
    var url = ENDPOINT + 'maps'
    return AXIOSAPI.post(url, data)
  }

  static updateMaps (id, data) {
    var url = ENDPOINT + 'maps/' + id
    return AXIOSAPI.put(url, data)
  }

  static deleteMaps (id) {
    var url = ENDPOINT + 'maps/' + id
    return AXIOSAPI.delete(url)
  }

  static getThingsForMap (params) {
    return AXIOSAPI.get('things', { baseURL: ENDPOINT, 'params': params })
  }

  static getAdhocReportJson (report) {
    let url = ENDPOINT + 'reports/adhoc'
    return AXIOSAPI.post(url, report)
  }

  static getAdhocReportBlob (report) {
    let url = ENDPOINT + 'reports/adhoc'
    return AXIOSAPI.post(url, report, {
      responseType: 'blob'
    })
  }

  /* Site Settings */

  static putSiteSettings (properties) {
    let url = ENDPOINT + 'orgsettings'
    return AXIOSAPI.post(url, properties)
  }

  static oktaAvailable () {
    let url = '/auth/okta/available'
    return AXIOSAPI.get(url)
  }

  static entraAvailable () {
    let url = '/auth/entra/available'
    return AXIOSAPI.get(url)
  }
}

export default ApiRequests



// WEBPACK FOOTER //
// ./src/api/ApiRequests.js